import type {
  LeDataFeedbackRating,
  LeDataProvenance,
  LeDataProvenanceTable,
  LeDataProvenanceTerm,
} from '~/types/leData';

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  return raw as Record<string, unknown>;
}

function asNonEmptyString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.trim() ? raw : undefined;
}

function parseTables(raw: unknown): Array<LeDataProvenanceTable> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const tables: Array<LeDataProvenanceTable> = [];
  for (const item of raw) {
    const entry = asRecord(item);
    const fqn = asNonEmptyString(entry?.fqn);
    if (!entry || !fqn) {
      continue;
    }
    const table: LeDataProvenanceTable = { fqn };
    const tier = asNonEmptyString(entry.tier);
    if (tier) {
      table.tier = tier;
    }
    const cadence = asNonEmptyString(entry.cadence);
    if (cadence) {
      table.cadence = cadence;
    }
    tables.push(table);
  }
  return tables;
}

function parseTerms(raw: unknown): Array<LeDataProvenanceTerm> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const terms: Array<LeDataProvenanceTerm> = [];
  for (const item of raw) {
    const entry = asRecord(item);
    const name = asNonEmptyString(entry?.name);
    if (!entry || !name) {
      continue;
    }
    terms.push({ name, verified: entry.verified === true });
  }
  return terms;
}

/**
 * Parses a `provenance` SSE payload or persisted session-message metadata into {@link LeDataProvenance}.
 * Defensive: accepts snake_case aliases and payloads carrying only `traceId` (older le-data servers).
 * Returns `null` when no provenance-shaped fields are present.
 */
export function parseLeDataProvenance(raw: unknown): LeDataProvenance | null {
  const obj = asRecord(raw);
  if (!obj) {
    return null;
  }

  const sessionId = asNonEmptyString(obj.sessionId) ?? asNonEmptyString(obj.session_id);
  const traceId = asNonEmptyString(obj.traceId) ?? asNonEmptyString(obj.trace_id);
  const confidence = typeof obj.confidence === 'number' && Number.isFinite(obj.confidence) ? obj.confidence : null;
  const tables = parseTables(obj.tables ?? obj.tables_used ?? obj.tablesUsed);
  const terms = parseTerms(obj.terms ?? obj.terms_matched ?? obj.termsMatched);

  if (!traceId && confidence === null && tables.length === 0 && terms.length === 0) {
    return null;
  }

  const provenance: LeDataProvenance = { confidence, tables, terms };
  if (sessionId) {
    provenance.sessionId = sessionId;
  }
  if (traceId) {
    provenance.traceId = traceId;
  }
  return provenance;
}

/** True when the provenance carries anything worth rendering in the answer footer (not just a bare `traceId`). */
export function hasLeDataProvenanceDisplayData(provenance: LeDataProvenance): boolean {
  return provenance.confidence !== null || provenance.tables.length > 0 || provenance.terms.length > 0;
}

export type LeDataConfidenceBucket = 'high' | 'medium' | 'low';

/** Display words for each confidence bucket. Single source of truth for the UI. */
export const CONFIDENCE_BUCKET_LABEL: Record<LeDataConfidenceBucket, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Buckets a numeric provenance `confidence` into a presentable band.
 * Accepts either a 0..1 fraction (e.g. `0.87`) or a 0..100 percent (e.g. `87`); values > 1 are
 * treated as a percent and divided by 100, then clamped to [0, 1]. As a consequence the (1, 2)
 * range is a dead zone that always maps to `'low'` (e.g. `1.5` -> `0.015`); this is acceptable
 * because a real confidence fraction never exceeds 1.0.
 * Returns `null` for null/undefined/non-finite input.
 *
 * Bands: high >= 0.75, medium >= 0.5 and < 0.75, low < 0.5.
 * The 0.75 high floor matches le-data's server-side confidence_band (HIGH_CONFIDENCE_FLOOR)
 * so the displayed band never disagrees with the band the agent branched on.
 */
export function getConfidenceBucket(
  confidence: number | null | undefined,
): LeDataConfidenceBucket | null {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) {
    return null;
  }
  const normalized = Math.min(1, Math.max(0, confidence > 1 ? confidence / 100 : confidence));
  if (normalized >= 0.75) {
    return 'high';
  }
  if (normalized >= 0.5) {
    return 'medium';
  }
  return 'low';
}

/** Accepts `'up' | 'down'`, le-data `'thumbs_up' | 'thumbs_down'`, or an object carrying either under `rating`/`feedback_type`. */
export function parseLeDataFeedbackRating(raw: unknown): LeDataFeedbackRating | undefined {
  if (raw === 'up' || raw === 'thumbs_up') {
    return 'up';
  }
  if (raw === 'down' || raw === 'thumbs_down') {
    return 'down';
  }
  const obj = asRecord(raw);
  if (obj) {
    return parseLeDataFeedbackRating(obj.rating ?? obj.feedback_type ?? obj.feedbackType);
  }
  return undefined;
}
